import type {
    CancellationPolicyResponse,
    CancellationPolicyRefundOptionResponse,
    CancellationPolicyRefundOptionResponseCancellationFee,
    SelfServiceOrderItemRefundOptionResponseRefundMethodsInner,
} from '@bookingkit/api-v4/dist/models';
import { CancellationPoliciesServiceError } from '../Errors/CancellationPoliciesServiceError';
import type {
    CancellationPolicy,
    PolicyCancellationFee,
    PolicyRefundMethod,
    PolicyRefundOption,
} from '../types';

const mapCancellationFee = (apiFee: CancellationPolicyRefundOptionResponseCancellationFee): PolicyCancellationFee => {
    if (apiFee.type === 'PERCENTAGE') {
        if (apiFee.value === 0) return { type: 'NONE' };
        return {
            type: 'PERCENTAGE',
            name: apiFee.name ?? '',
            description: apiFee.description ?? undefined,
            value: apiFee.value ?? 0,
        };
    }
    if (apiFee.type === 'FIXED') {
        const first = apiFee.amount?.[0];
        if (!first || first.value === 0) return { type: 'NONE' };
        return {
            type: 'FIXED',
            name: apiFee.name ?? '',
            description: apiFee.description ?? undefined,
            amount: first.value ?? 0,
            currency: first.currency ?? 'EUR',
        };
    }
    return { type: 'NONE' };
};

const mapRefundMethods = (methods: SelfServiceOrderItemRefundOptionResponseRefundMethodsInner[]): PolicyRefundMethod[] =>
    methods.map((m) => ({ type: m.type as PolicyRefundMethod['type'] }));

const mapRefundOption = (apiOption: CancellationPolicyRefundOptionResponse): PolicyRefundOption => ({
    id: apiOption.id ?? crypto.randomUUID(),
    cancellationFee: mapCancellationFee(apiOption.cancellation_fee),
    refundMethods: mapRefundMethods(apiOption.refund_methods ?? []),
});

export const fromApiModel = (response: CancellationPolicyResponse): CancellationPolicy => {
    if (typeof response.id !== 'string') {
        throw CancellationPoliciesServiceError.fromInvalidProperty('id', 'string', response.id);
    }
    if (typeof response.name !== 'string') {
        throw CancellationPoliciesServiceError.fromInvalidProperty('name', 'string', response.name);
    }

    const vp = response.restrictions?.validity_period;

    return {
        id: response.id,
        name: response.name,
        description: response.description ?? '',
        type: response.type,
        validityType: vp?.type ?? 'AFTER_PURCHASE_DATE',
        units: vp?.units ?? 'DAYS',
        value: vp?.value ?? 14,
        refundOptions: (response.refund_options ?? []).map(mapRefundOption),
    };
};
